/** One row of the claims grid (US-009 AC-003). Line entries are nested in {@link ClaimRow.lines}. */
export interface ClaimRow {
  readonly claimNumber: string;
  readonly npi: string | null;
  /** Internal control number. `icnNumber` on a claim entry upstream — see ClaimContracts.cs. */
  readonly icn: string | null;
  readonly location: string | null;
  readonly procCode: string | null;
  readonly svcCode: string | null;
  readonly serviceDateFrom: string | null;
  readonly serviceDateTo: string | null;
  readonly paymentDate: string | null;
  readonly status: string | null;
  readonly billedAmount: number | null;
  readonly medicareAllowedAmount: number | null;
  readonly paidAmount: number | null;
  /** What the carrier paid (PP-014). `carrierPaidAmount` upstream, on the header and on every entry. */
  readonly carrierPaidAmount: number | null;
  /** TODO: no source in the live BF payload; the mock supplies it (US-012 AC-002). */
  readonly coinsurance: number | null;
  readonly checkNumber: string | null;
  /** Set on entries only: the live payload carries no reason on the claim header. */
  readonly reason: string | null;
  /** TODO: no source in the live BF payload; the mock supplies it. */
  readonly description: string | null;
  /** The claim's individual entries, `claimLines` upstream. Always empty on an entry itself. */
  readonly lines: readonly ClaimRow[];
}

export interface ClaimsResult {
  readonly claims: readonly ClaimRow[];
  /** What the policy actually holds, which is more than `claims` when the response was truncated. */
  readonly totalCount: number;
  /** True when the policy has more claims than the server returns in one response. */
  readonly truncated: boolean;
}

/** Columns the grid can sort by (US-010 AC-002). */
export type SortableColumn =
  | 'claimNumber'
  | 'npi'
  | 'icn'
  | 'location'
  | 'procCode'
  | 'svcCode'
  | 'serviceDateFrom'
  | 'paymentDate'
  | 'status'
  | 'billedAmount'
  | 'medicareAllowedAmount'
  | 'paidAmount'
  | 'carrierPaidAmount'
  | 'checkNumber';

export interface SortState {
  readonly column: SortableColumn;
  readonly direction: 'asc' | 'desc';
}

/** Every category of the filter panel (US-014 AC-002). */
export interface ClaimFilters {
  readonly claimNumber: string;
  readonly npi: string;
  readonly icn: string;
  readonly location: string;
  readonly procCode: string;
  readonly svcCode: string;
  readonly serviceDateFrom: string;
  readonly serviceDateTo: string;
  readonly statuses: readonly string[];
  readonly reason: string;
  readonly billedAmount: string;
  readonly medicareAllowedAmount: string;
  readonly paidAmount: string;
  readonly paymentDateFrom: string;
  readonly paymentDateTo: string;
  readonly checkNumber: string;
}

export const EMPTY_FILTERS: ClaimFilters = {
  claimNumber: '',
  npi: '',
  icn: '',
  location: '',
  procCode: '',
  svcCode: '',
  serviceDateFrom: '',
  serviceDateTo: '',
  statuses: [],
  reason: '',
  billedAmount: '',
  medicareAllowedAmount: '',
  paidAmount: '',
  paymentDateFrom: '',
  paymentDateTo: '',
  checkNumber: '',
};
